import {dynamic} from '@github-ui/dynamic'
import type {ValidationResult} from '@github-ui/entity-validators'
import {noop} from '@github-ui/noop'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import type React from 'react'
import {Suspense, useCallback, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import {VALUES} from '../../constants/values'
import {CommentLoading} from '../CommentLoading'
import type {IssueComment_issueComment$key} from './__generated__/IssueComment_issueComment.graphql'
import {IssueCommentViewer} from './IssueCommentViewer'

const LazyIssueCommentEditor = dynamic(
  async () => {
    const m = await import('./IssueCommentEditor')
    return {default: m.IssueCommentEditor}
  },
  {displayName: 'IssueCommentEditor'},
)

export type IssueCommentProps = {
  anchorBaseUrl?: string
  comment: IssueComment_issueComment$key
  onLinkClick?: (event: MouseEvent) => void
  onChange?: () => void
  onEditCancel?: () => void
  onReply?: (quotedComment: string) => void
  onSave?: () => void
  refAttribute?: React.RefObject<HTMLDivElement | null>
  highlightedCommentId?: string
  relayConnectionIds?: string[]
  navigate: (url: string) => void
  commentSubjectAuthorLogin?: string
  showCopilotMenuItem?: boolean
}

export function IssueComment({
  anchorBaseUrl,
  comment,
  navigate,
  onLinkClick,
  onChange = noop,
  onEditCancel = noop,
  onReply = noop,
  onSave = noop,
  refAttribute,
  highlightedCommentId,
  relayConnectionIds,
  commentSubjectAuthorLogin,
  showCopilotMenuItem = false,
}: IssueCommentProps) {
  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [hasDisabledActions, setHasDisabledActions] = useState(false)
  const [manualValidationResult, setManualValidationResult] = useState<ValidationResult | undefined>(undefined)
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment IssueComment_issueComment on IssueComment {
        ...IssueCommentViewerCommentRow
        ...IssueCommentViewerReactable

        ...IssueCommentEditorBodyFragment
        repository {
          slashCommandsEnabled
          nameWithOwner
          databaseId
        }
        issue {
          id
        }
        id
        createdViaEmail
      }
    `,
    comment,
  )

  const [presavedComment, setPresavedComment, resetPresavedComment] = useSessionStorage<string>(
    VALUES.localStorageKeys.issueComment(
      'issuecomment',
      data?.issue?.id ?? `fallback-${data?.id ?? 'unknown'}`,
      data?.id ?? '',
    ),
    '',
  )

  const commentSaved = useCallback(() => {
    setIsEditing(false)
    onSave()
    resetPresavedComment()
  }, [onSave, resetPresavedComment])

  const onCancel = () => {
    setIsEditing(false)
    resetPresavedComment()
    onEditCancel()
  }

  const onCommentChange = useCallback(
    (value: string) => {
      setPresavedComment(value)
      onChange()
    },
    [onChange, setPresavedComment],
  )

  if (!data) {
    return null
  }

  return (
    <>
      {isEditing || isSubmitting || manualValidationResult ? (
        <Suspense fallback={<CommentLoading />}>
          <LazyIssueCommentEditor
            initialValidationResult={manualValidationResult}
            setManualValidationResult={setManualValidationResult}
            setIsEditing={setIsEditing}
            setIsSubmitting={setIsSubmitting}
            setHasDisabledActions={setHasDisabledActions}
            isSubmitting={isSubmitting}
            cancelEdit={onCancel}
            onSave={commentSaved}
            comment={data}
            repo={data}
            initialValue={presavedComment}
            onChange={onCommentChange}
          />
        </Suspense>
      ) : (
        <IssueCommentViewer
          anchorBaseUrl={anchorBaseUrl}
          comment={data}
          reactable={data}
          setIsEditing={() => setIsEditing(true)}
          onReply={onReply}
          onLinkClick={onLinkClick}
          refAttribute={refAttribute}
          navigate={navigate}
          highlightedCommentId={highlightedCommentId}
          relayConnectionIds={relayConnectionIds}
          commentSubjectAuthorLogin={commentSubjectAuthorLogin}
          createdViaEmail={data.createdViaEmail}
          hasDisabledActions={hasDisabledActions}
          showCopilotMenuItem={showCopilotMenuItem}
          onEditHover={LazyIssueCommentEditor.preload}
        />
      )}
    </>
  )
}
